import React, { useContext, useEffect, useState } from "react"

import Paper from "@material-ui/core/Paper"
import Container from "@material-ui/core/Container"
import Grid from "@material-ui/core/Grid"
import { makeStyles } from "@material-ui/core/styles"
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import HourglassEmptyOutlinedIcon from "@material-ui/icons/HourglassEmptyOutlined";
import DoneAllOutlinedIcon from "@material-ui/icons/DoneAllOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

import { i18n } from "../../translate/i18n";

import Chart from "./Chart"

const useStyles = makeStyles(theme => ({
	container: {
		paddingTop: theme.spacing(3),
		paddingBottom: theme.spacing(4),
	},
	pageHeader: {
		display: "flex",
		alignItems: "flex-end",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(1),
		[theme.breakpoints.down("xs")]: {
			alignItems: "flex-start",
			flexDirection: "column",
		},
	},
	pageEyebrow: {
		color: "#3154f5",
		fontSize: 11,
		fontWeight: 800,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
	},
	pageTitle: {
		fontWeight: 800,
		letterSpacing: 0,
		marginTop: theme.spacing(0.5),
	},
	pageSubtitle: {
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.75),
	},
	refreshHint: {
		background: theme.palette.type === "dark" ? "rgba(91, 115, 255, 0.2)" : "#eef3ff",
		borderRadius: 6,
		color: theme.palette.type === "dark" ? "#dbe7ff" : "#3154f5",
		fontSize: 12,
		fontWeight: 700,
		padding: theme.spacing(1, 1.5),
		whiteSpace: "nowrap",
	},
	overviewCard: {
		border: `1px solid ${theme.palette.type === "dark" ? "#2c3b55" : "#e5ebf3"}`,
		borderRadius: 8,
		boxShadow: "none",
		display: "flex",
		flexDirection: "column",
		minHeight: 144,
		padding: theme.spacing(2.25),
	},
	metricTop: {
		alignItems: "flex-start",
		display: "flex",
		justifyContent: "space-between",
	},
	metricLabel: {
		color: theme.palette.text.secondary,
		fontSize: 13,
		fontWeight: 700,
	},
	metricIcon: {
		background: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#f4f7fb",
		borderRadius: 6,
		padding: theme.spacing(0.8),
	},
	metricNumber: {
		fontSize: 34,
		fontWeight: 800,
		letterSpacing: 0,
		lineHeight: 1,
		marginTop: theme.spacing(2),
	},
	fixedHeightPaper: {
		padding: theme.spacing(2.25),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: 240,
		border: `1px solid ${theme.palette.type === "dark" ? "#2c3b55" : "#e5ebf3"}`,
		borderRadius: 8,
		boxShadow: "none",
	},
	customFixedHeightPaper: {
		padding: theme.spacing(2.25),
		display: "flex",
		overflow: "visible",
		flexDirection: "column",
		minHeight: 120,
		border: `1px solid ${theme.palette.type === "dark" ? "#2c3b55" : "#e5ebf3"}`,
		borderRadius: 8,
		boxShadow: "none",
	},
	slaPaper: {
		padding: theme.spacing(2.25),
		display: "flex",
		overflow: "visible",
		flexDirection: "column",
		minHeight: 140,
		border: `1px solid ${theme.palette.type === "dark" ? "#2c3b55" : "#e5ebf3"}`,
		borderRadius: 8,
		boxShadow: "none",
	},
	slaPaperAlert: {
		borderLeft: "5px solid #d32f2f",
		backgroundColor: theme.palette.type === "dark" ? "#3b1f1f" : "#fff4f4",
	},
	customFixedHeightPaperLg: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: "100%",
	},
	sectionPaper: {
		padding: theme.spacing(2.25),
		display: "flex",
		flexDirection: "column",
		minHeight: 260,
		border: `1px solid ${theme.palette.type === "dark" ? "#2c3b55" : "#e5ebf3"}`,
		borderRadius: 8,
		boxShadow: "none",
	},
	metricValue: {
		marginTop: "auto",
	},
	metricHelp: {
		color: theme.palette.text.secondary,
		fontSize: 12,
	},
	listRow: {
		display: "grid",
		gridTemplateColumns: "1fr auto",
		gap: theme.spacing(1),
		alignItems: "center",
		padding: theme.spacing(1, 0),
	},
	queueDot: {
		width: 10,
		height: 10,
		borderRadius: "50%",
		display: "inline-block",
		marginRight: theme.spacing(1),
	},
	statusLine: {
		color: theme.palette.text.secondary,
		fontSize: 12,
	},
}))

const initialMetrics = {
	counts: {
		open: 0,
		pending: 0,
		closed: 0,
	},
	overview: {
		waitingOverSla: 0,
		withoutFirstResponse: 0,
		avgFirstResponseMinutes: 0,
		oldestWaitingMinutes: 0,
	},
	queues: [],
	users: [],
	waitingTickets: [],
	slaMinutes: 30,
};

const formatMinutes = value => {
	const minutes = Number(value || 0);

	if (minutes < 60) {
		return `${minutes} min`;
	}

	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;

	return rest > 0 ? `${hours} h ${rest} min` : `${hours} h`;
};

const normalizeMetrics = data => ({
	...initialMetrics,
	...(data || {}),
	counts: {
		...initialMetrics.counts,
		...(data?.counts || {}),
	},
	overview: {
		...initialMetrics.overview,
		...(data?.overview || {}),
	},
	queues: Array.isArray(data?.queues) ? data.queues : [],
	users: Array.isArray(data?.users) ? data.users : [],
	waitingTickets: Array.isArray(data?.waitingTickets) ? data.waitingTickets : [],
});

const Dashboard = () => {
	const classes = useStyles()
	const [metrics, setMetrics] = useState(initialMetrics);
	const [loadingMetrics, setLoadingMetrics] = useState(true);

	const { user } = useContext(AuthContext);
	var userQueueIds = [];

	if (user?.queues && user.queues.length > 0) {
		userQueueIds = user.queues.map(q => q.id);
	}

	useEffect(() => {
		const fetchMetrics = async () => {
			try {
				const { data } = await api.get("/dashboard/metrics", {
					params: {
						queueIds: JSON.stringify(userQueueIds),
					},
				});
				setMetrics(normalizeMetrics(data));
			} catch (err) {
				setMetrics(initialMetrics);
				toastError(err);
			} finally {
				setLoadingMetrics(false);
			}
		};

		fetchMetrics();
	}, [JSON.stringify(userQueueIds)]);

	return (
		<div>
			<Container maxWidth="xl" className={classes.container}>
				<div className={classes.pageHeader}>
					<Typography className={classes.pageSubtitle}>Tickets, SLA y carga del equipo en una sola vista.</Typography>
					<div className={classes.refreshHint}>SLA configurado: {metrics.slaMinutes} min</div>
				</div>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={4}>
						<Paper className={classes.overviewCard}>
							<div className={classes.metricTop}>
								<Typography className={classes.metricLabel}>{i18n.t("dashboard.messages.inAttendance.title")}</Typography>
								<InboxOutlinedIcon className={classes.metricIcon} style={{ color: "#3154f5" }} />
							</div>
							<Typography className={classes.metricNumber}>{loadingMetrics ? "-" : metrics.counts.open}</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Paper className={classes.overviewCard}>
							<div className={classes.metricTop}>
								<Typography className={classes.metricLabel}>{i18n.t("dashboard.messages.waiting.title")}</Typography>
								<HourglassEmptyOutlinedIcon className={classes.metricIcon} style={{ color: "#d97706" }} />
							</div>
							<Typography className={classes.metricNumber}>{loadingMetrics ? "-" : metrics.counts.pending}</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Paper className={classes.overviewCard}>
							<div className={classes.metricTop}>
								<Typography className={classes.metricLabel}>{i18n.t("dashboard.messages.closed.title")}</Typography>
								<DoneAllOutlinedIcon className={classes.metricIcon} style={{ color: "#16a34a" }} />
							</div>
							<Typography className={classes.metricNumber}>{loadingMetrics ? "-" : metrics.counts.closed}</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} md={3}>
						<Paper
							className={`${classes.slaPaper} ${
								metrics.overview.waitingOverSla > 0 ? classes.slaPaperAlert : ""
							}`}
						>
							<Typography component="h3" variant="subtitle1" color="primary">
								{i18n.t("dashboard.sla.overdue")}
							</Typography>
							<Typography className={classes.metricHelp}>
								{i18n.t("dashboard.sla.overdueHelp", { minutes: metrics.slaMinutes })}
							</Typography>
							<Typography className={classes.metricValue} component="h1" variant="h4">
								{loadingMetrics ? "-" : metrics.overview.waitingOverSla}
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} md={3}>
						<Paper className={classes.slaPaper}>
							<Typography component="h3" variant="subtitle1" color="primary">
								{i18n.t("dashboard.sla.withoutFirstResponse")}
							</Typography>
							<Typography className={classes.metricHelp}>
								{i18n.t("dashboard.sla.withoutFirstResponseHelp")}
							</Typography>
							<Typography className={classes.metricValue} component="h1" variant="h4">
								{loadingMetrics ? "-" : metrics.overview.withoutFirstResponse}
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} md={3}>
						<Paper className={classes.slaPaper}>
							<Typography component="h3" variant="subtitle1" color="primary">
								{i18n.t("dashboard.sla.avgFirstResponse")}
							</Typography>
							<Typography className={classes.metricHelp}>
								{i18n.t("dashboard.sla.avgFirstResponseHelp")}
							</Typography>
							<Typography className={classes.metricValue} component="h1" variant="h4">
								{loadingMetrics ? "-" : formatMinutes(metrics.overview.avgFirstResponseMinutes)}
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} md={3}>
						<Paper className={classes.slaPaper}>
							<Typography component="h3" variant="subtitle1" color="primary">
								{i18n.t("dashboard.sla.oldestWaiting")}
							</Typography>
							<Typography className={classes.metricHelp}>
								{i18n.t("dashboard.sla.oldestWaitingHelp")}
							</Typography>
							<Typography className={classes.metricValue} component="h1" variant="h4">
								{loadingMetrics ? "-" : formatMinutes(metrics.overview.oldestWaitingMinutes)}
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12}>
						<Paper className={classes.fixedHeightPaper}>
							<Chart />
						</Paper>
					</Grid>
					<Grid item xs={12} md={4}>
						<Paper className={classes.sectionPaper}>
							<Typography component="h3" variant="h6" color="primary">
								{i18n.t("dashboard.sections.byQueue")}
							</Typography>
							{metrics.queues.length === 0 && (
								<Typography className={classes.statusLine}>
									{i18n.t("dashboard.empty")}
								</Typography>
							)}
							{metrics.queues.map(queue => (
								<React.Fragment key={queue.queueId || queue.queueName}>
									<div className={classes.listRow}>
										<div>
											<Typography variant="body2">
												<span
													className={classes.queueDot}
													style={{ backgroundColor: queue.color || "#9e9e9e" }}
												/>
												{queue.queueName}
											</Typography>
											<Typography className={classes.statusLine}>
												{i18n.t("dashboard.labels.pending")}: {queue.pending} · {i18n.t("dashboard.labels.open")}: {queue.open}
											</Typography>
										</div>
										<Typography variant="h6">{queue.total}</Typography>
									</div>
									<Divider />
								</React.Fragment>
							))}
						</Paper>
					</Grid>
					<Grid item xs={12} md={4}>
						<Paper className={classes.sectionPaper}>
							<Typography component="h3" variant="h6" color="primary">
								{i18n.t("dashboard.sections.byAgent")}
							</Typography>
							{metrics.users.length === 0 && (
								<Typography className={classes.statusLine}>
									{i18n.t("dashboard.empty")}
								</Typography>
							)}
							{metrics.users.map(agent => (
								<React.Fragment key={agent.userId || agent.userName}>
									<div className={classes.listRow}>
										<div>
											<Typography variant="body2">{agent.userName}</Typography>
											<Typography className={classes.statusLine}>
												{i18n.t("dashboard.labels.open")}: {agent.open} · {i18n.t("dashboard.labels.unread")}: {agent.unreadMessages}
											</Typography>
										</div>
										<Typography variant="h6">{agent.total}</Typography>
									</div>
									<Divider />
								</React.Fragment>
							))}
						</Paper>
					</Grid>
					<Grid item xs={12} md={4}>
						<Paper className={classes.sectionPaper}>
							<Typography component="h3" variant="h6" color="primary">
								{i18n.t("dashboard.sections.waitingTickets")}
							</Typography>
							{metrics.waitingTickets.length === 0 && (
								<Typography className={classes.statusLine}>
									{i18n.t("dashboard.empty")}
								</Typography>
							)}
							{metrics.waitingTickets.map(ticket => (
								<React.Fragment key={ticket.id}>
									<div className={classes.listRow}>
										<div>
											<Typography variant="body2">{ticket.contactName}</Typography>
											<Typography className={classes.statusLine}>
												{ticket.queueName} · {formatMinutes(ticket.waitingMinutes)}
											</Typography>
										</div>
										<Typography variant="caption">#{ticket.id}</Typography>
									</div>
									<Divider />
								</React.Fragment>
							))}
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</div>
	)
}

export default Dashboard
